import {
  CustomerSearchDataFragment,
  SearchProfileOptionDataFragment,
  SearchProfileType,
  SearchSearchProfiles,
  useUpsertSearchProfileMutation
} from "@graphql";
import locale from "@bonliva-crm/locale";
import {
  CloseIcon,
  Color,
  Modal,
  SelectMenu,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { CustomerFullSearchModal } from "@customers-new/customer-search/full/customer-full-search-modal";
import { Box, Chip, TextField, Typography, makeStyles } from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import { transparentize } from "polished";
import { useEffect, useState } from "react";

type Props = {
  open: boolean;
  mode: "new" | "edit";
  setOpen: (open: boolean) => void;
  currentSearchProfile: SearchProfileOptionDataFragment | null;
  fields: string;
  type: SearchProfileType;
  onlyName?: boolean; // If true, only the name field is shown
};

export const UpsertSearchProfileModal = (props: Props) => {
  const snack = useSnackbarsPipeline();
  const [upsertSearchProfileMutation] = useUpsertSearchProfileMutation({
    refetchQueries: [SearchSearchProfiles.OperationName]
  });
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);

  const [customers, setCustomers] = useState<CustomerSearchDataFragment[]>([]);
  const [searchProfileName, setSearchProfileName] = useState<string>(
    props.mode === "edit" ? props.currentSearchProfile?.name ?? "" : ""
  );

  useEffect(() => {
    //TODO implement initial for customers, they are customer refs in search profile
    if (props.mode === "edit" && props.currentSearchProfile) {
      setSearchProfileName(props.currentSearchProfile.name);
    }
    if (props.mode === "new" && searchProfileName) {
      setSearchProfileName("");
    }
  }, [props.mode, props.currentSearchProfile]);

  const classes = useStyles();

  const upsertSearchProfile = () => {
    upsertSearchProfileMutation({
      variables: {
        id: props.mode === "edit" ? props.currentSearchProfile?._id : undefined,
        data: {
          _id: props.mode === "edit" ? props.currentSearchProfile?._id : undefined,
          customerRefs: customers.map((c) => ({
            Id: c._id,
            Name: c.Name,
            Type: c.Type
          })),
          fields: props.fields,
          name: searchProfileName,
          type: props.type
        }
      }
    }).then((res) => {
      snack.showSuccess(locale.SearchProfileSaved);
    });
  };

  const modalContent = (
    <Box className={classes.root}>
      <TextField
        label={locale.Title}
        value={searchProfileName}
        onChange={(e) => setSearchProfileName(e.target.value)}
      />
      {!props.onlyName && (
        <>
          <Box>
            <SelectMenu
              open={customerSearchOpen}
              setOpen={setCustomerSearchOpen}
              label={locale["Customer name"]}
              openIcon={<ArrowForward htmlColor={Color.Body} className={classes.arrow} />}
              value={
                <Box className={classes.chipContainer}>
                  {customers.map((customer, index) => (
                    <Chip
                      key={`customer-${customer._id}-${index}`}
                      size="small"
                      variant="outlined"
                      label={
                        <Typography variant="subtitle1" style={{ color: Color.Body }}>
                          {customer.Name}
                        </Typography>
                      }
                      deleteIcon={<CloseIcon style={{ color: Color.Headings }} />}
                      onDelete={() =>
                        setCustomers(customers.filter((c) => c._id !== customer._id))
                      }
                      className={classes.chip}
                    />
                  ))}
                </Box>
              }
            />
          </Box>
          <CustomerFullSearchModal
            open={customerSearchOpen}
            setOpen={setCustomerSearchOpen}
            value={customers}
            onChange={(v) => setCustomers(v)}
            isMulti={true}
          />{" "}
        </>
      )}
    </Box>
  );

  return (
    <Modal
      open={props.open}
      setOpen={props.setOpen}
      onClose={() => {
        if (props.mode === "new") {
          setCustomers([]);
          setSearchProfileName("");
        }
      }}
      onSubmit={upsertSearchProfile}
      saveButtonDisabled={searchProfileName === ""}
      label={locale.SearchProfile}
      modalContent={modalContent}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  },
  arrow: {
    width: "1rem",
    height: "1rem",
    marginLeft: "auto"
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem"
  },
  chip: {
    padding: "2px 4px 2px 2px",
    backgroundColor: transparentize(0.75, Color.DoveBlue)
  }
}));
